import {
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Collection, composeRenderProps } from 'react-aria-components';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Header, ListBox, ListBoxSection, ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { SearchResultsContext } from './contexts';
import type { SearchResultsData, SearchResultsProps, Section, SectionedData, WithKey } from './types';

import styles from './ResourceSelector.module.css';

// Default function to extract a unique key from any object
const defaultGetItemKey = <T extends object>(item: T): string => {
  const itemAsRecord = item as Record<string, unknown>;
  // Try common id patterns, fall back to JSON stringify
  return String(itemAsRecord.key || itemAsRecord.id || itemAsRecord._id || itemAsRecord.uuid || JSON.stringify(item));
};

// Helper function to check if data is sectioned
const isSectionedData = <T extends object>(items: SearchResultsData<T>): items is SectionedData<T> => {
  return (
    Array.isArray(items) &&
    items.length > 0 &&
    typeof items[0] === 'object' &&
    items[0] !== null &&
    'name' in items[0] &&
    'items' in items[0]
  );
};

// Marks the element the hovered preview is scoped to: hover is released once the pointer leaves it
const DETAILS_HOVER_BOUNDARY_ATTRIBUTE = 'data-details-hover-boundary';

export const detailsHoverBoundaryProps = { [DETAILS_HOVER_BOUNDARY_ATTRIBUTE]: '' };

// Announced on the boundary when a list takes over the preview, so lists sharing one hand the preview over
const DETAILS_PREVIEW_CLAIM_EVENT = 'lp-details-preview-claim';

// Announced when the pointer leaves the boundary, so the list that was previewing keeps the panel
// without outranking a sibling holding the selection
const DETAILS_PREVIEW_RELEASE_EVENT = 'lp-details-preview-release';

const boundaryListIds = new WeakMap<Element, Set<string>>();

const joinBoundary = (boundary: Element, listId: string) => {
  const listIds = boundaryListIds.get(boundary) ?? new Set<string>();
  listIds.add(listId);
  boundaryListIds.set(boundary, listIds);
};

const leaveBoundary = (boundary: Element, listId: string) => {
  boundaryListIds.get(boundary)?.delete(listId);
};

const hasSiblingLists = (boundary: Element | null, listId: string) => {
  const listIds = boundary ? boundaryListIds.get(boundary) : undefined;

  return !!listIds && Array.from(listIds).some((id) => id !== listId);
};

const boundaryFocusOwners = new WeakMap<Element, Set<string>>();

const setFocusOwner = (boundary: Element | null, listId: string, holdsFocus: boolean) => {
  if (!boundary) {
    return;
  }

  const focusOwners = boundaryFocusOwners.get(boundary) ?? new Set<string>();
  if (holdsFocus) {
    focusOwners.add(listId);
  } else {
    focusOwners.delete(listId);
  }
  boundaryFocusOwners.set(boundary, focusOwners);
};

const hasSiblingFocus = (boundary: Element | null, listId: string) => {
  const focusOwners = boundary ? boundaryFocusOwners.get(boundary) : undefined;

  return !!focusOwners && Array.from(focusOwners).some((id) => id !== listId);
};

// Slot component for ResourceSelector
function SearchResultsInternal<T extends object = object>({
  items,
  children,
  onDetailsItemChange,
  selectedKeys,
  getItemKey = defaultGetItemKey,
  renderEmptyState,
  isLoading = false,
  isLoadingMore,
  isError = false,
  onLoadMore,
  hasMore,
  resultsAreaClassName,
  slot,
  ...props
}: SearchResultsProps<T>) {
  // Get props from context when used as a slot
  const contextProps = useContext(SearchResultsContext);

  // Merge local props with context props, local props take precedence
  const mergedProps = {
    selectedKeys: selectedKeys ?? contextProps?.selectedKeys,
    onSelectionChange: props.onSelectionChange ?? contextProps?.onSelectionChange,
    selectionMode: props.selectionMode ?? contextProps?.selectionMode,
    onDetailsItemChange: onDetailsItemChange ?? contextProps?.onDetailsItemChange,
    renderEmptyState: renderEmptyState ?? contextProps?.renderEmptyState,
    isLoadingMore: isLoadingMore ?? contextProps?.isLoadingMore ?? false,
    onLoadMore: onLoadMore ?? contextProps?.onLoadMore,
    hasMore: hasMore ?? contextProps?.hasMore ?? false,
  };

  // Use merged props for infinite scroll logic
  const shouldEnableInfiniteScroll = mergedProps.onLoadMore && mergedProps.hasMore;

  // State for tracking selection changes
  const [lastSelectedKey, setLastSelectedKey] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [focusedKeyEntry, setFocusedKeyEntry] = useState<{ key: string; signature: string } | null>(null);
  const [previewClaim, setPreviewClaim] = useState<{ listId: string | null; isActive: boolean; version: number }>({
    listId: null,
    isActive: false,
    version: 0,
  });
  const [listBoxElement, setListBoxElement] = useState<HTMLDivElement | null>(null);
  // Rendered in every state, including a custom empty state, so the declared boundary is reachable without a list box
  const boundaryAnchorRef = useRef<HTMLElement | null>(null);
  const setBoundaryAnchor = (element: HTMLElement | null) => {
    boundaryAnchorRef.current = element;
  };
  const hoveredKeyRef = useRef<string | null>(null);
  const focusedKeyRef = useRef<string | null>(null);
  const holdsActiveClaimRef = useRef(false);
  const hoverBoundaryRef = useRef<Element | null>(null);
  const selectedKeysSignatureRef = useRef('');
  const listId = useId();

  // Use a callback ref to get better control over when the element is set
  const listBoxRef = (element: HTMLDivElement | null) => {
    setListBoxElement(element);
  };

  const claimPreview = useCallback(() => {
    holdsActiveClaimRef.current = true;
    hoverBoundaryRef.current?.dispatchEvent(new CustomEvent(DETAILS_PREVIEW_CLAIM_EVENT, { detail: listId }));
  }, [listId]);

  // Released once neither the pointer nor focus is in this list, so a sibling holding the selection can take over
  const releasePreviewWhenIdle = useCallback(() => {
    if (!holdsActiveClaimRef.current || hoveredKeyRef.current !== null || focusedKeyRef.current !== null) {
      return;
    }

    holdsActiveClaimRef.current = false;
    hoverBoundaryRef.current?.dispatchEvent(new CustomEvent(DETAILS_PREVIEW_RELEASE_EVENT, { detail: listId }));
  }, [listId]);

  // Process items - handle both flat and sectioned data
  const processedData = useMemo(() => {
    if (!items) {
      return { isFlat: true, flatItems: [], sectionedItems: [], keyToOriginalItem: new Map() };
    }

    // Map to track original items by their keys
    const keyToOriginalItem = new Map<string, T>();

    if (isSectionedData<T>(items)) {
      // Sectioned data
      const sectionsWithKeys: Array<Section<WithKey<T>>> = items.map((section) => ({
        name: section.name,
        items: section.items.map((item) => {
          const key = 'key' in item ? item.key : getItemKey(item);
          keyToOriginalItem.set(String(key), item);
          return {
            ...item,
            key: String(key),
          };
        }),
      }));

      // Also flatten for compatibility with existing logic - items already have keys
      const allFlatItems: Array<WithKey<T>> = sectionsWithKeys.flatMap((section) => section.items);

      return {
        isFlat: false,
        flatItems: allFlatItems,
        sectionedItems: sectionsWithKeys,
        keyToOriginalItem,
      };
    } else {
      // Flat data
      const itemsArray = Array.from(items);
      const flatItems: Array<WithKey<T>> = itemsArray.map((item) => {
        const key = 'key' in item ? item.key : getItemKey(item);
        keyToOriginalItem.set(String(key), item);
        return {
          ...item,
          key: String(key),
        };
      });

      return {
        isFlat: true,
        flatItems,
        sectionedItems: [],
        keyToOriginalItem,
      };
    }
  }, [items, getItemKey]);

  const itemsWithKeys = processedData.flatItems;

  const firstItemKey = useMemo(() => (itemsWithKeys.length > 0 ? itemsWithKeys[0].key : null), [itemsWithKeys]);

  const selectedKeysSignature = useMemo(() => {
    if (!mergedProps.selectedKeys) {
      return '';
    }
    return mergedProps.selectedKeys === 'all' ? 'all' : Array.from(mergedProps.selectedKeys).join('\u0000');
  }, [mergedProps.selectedKeys]);

  // A new selection outranks the focused item, but not hover: the pointer still rests on its item
  const focusedKey = focusedKeyEntry?.signature === selectedKeysSignature ? focusedKeyEntry.key : null;

  useEffect(() => {
    selectedKeysSignatureRef.current = selectedKeysSignature;
  }, [selectedKeysSignature]);

  // Track last selected item when selection changes
  const selectedKeysForLast = mergedProps.selectedKeys;
  const [lastSelectedSync, setLastSelectedSync] = useState<{
    selectedKeys: typeof selectedKeysForLast | undefined;
    itemsWithKeys: typeof itemsWithKeys | undefined;
  }>({
    selectedKeys: undefined,
    itemsWithKeys: undefined,
  });
  if (
    selectedKeysForLast &&
    selectedKeysForLast !== 'all' &&
    (lastSelectedSync.selectedKeys !== selectedKeysForLast || lastSelectedSync.itemsWithKeys !== itemsWithKeys)
  ) {
    setLastSelectedSync({ selectedKeys: selectedKeysForLast, itemsWithKeys });
    const selectedKeysArray = Array.from(selectedKeysForLast);
    if (selectedKeysArray.length > 0) {
      const lastValidSelectedKey = selectedKeysArray
        .reverse()
        .find((key) => itemsWithKeys.some((item) => item.key === key));
      setLastSelectedKey(lastValidSelectedKey === undefined ? null : String(lastValidSelectedKey));
    } else {
      setLastSelectedKey(null);
    }
  }

  useEffect(() => {
    if (!listBoxElement) {
      return;
    }

    const findItemKey = (target: EventTarget | null) => {
      let element = target instanceof HTMLElement ? target : null;
      while (element && element !== listBoxElement) {
        const itemKey = element.getAttribute('data-key');
        if (itemKey) {
          return itemKey;
        }
        element = element.parentElement;
      }
      return null;
    };

    const handleFocusIn = (event: FocusEvent) => {
      const itemKey = findItemKey(event.target);
      if (itemKey) {
        focusedKeyRef.current = itemKey;
        setFocusedKeyEntry({ key: itemKey, signature: selectedKeysSignatureRef.current });
        setFocusOwner(hoverBoundaryRef.current, listId, true);
        claimPreview();
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const nextFocused = event.relatedTarget;
      if (!(nextFocused instanceof Node) || !listBoxElement.contains(nextFocused)) {
        focusedKeyRef.current = null;
        setFocusedKeyEntry(null);
        setFocusOwner(hoverBoundaryRef.current, listId, false);
        releasePreviewWhenIdle();
      }
    };

    listBoxElement.addEventListener('focusin', handleFocusIn);
    listBoxElement.addEventListener('focusout', handleFocusOut);

    return () => {
      listBoxElement.removeEventListener('focusin', handleFocusIn);
      listBoxElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [listBoxElement, listId, claimPreview, releasePreviewWhenIdle]);

  // A removed list box never reports focusout, so its focus is dropped here instead of blocking its siblings
  const listBoxRemoved = !listBoxElement && (focusedKeyEntry !== null || hoveredKey !== null);
  if (listBoxRemoved) {
    setFocusedKeyEntry(null);
    setHoveredKey(null);
  }
  useEffect(() => {
    if (listBoxElement) {
      return;
    }
    focusedKeyRef.current = null;
    hoveredKeyRef.current = null;
    setFocusOwner(hoverBoundaryRef.current, listId, false);
    releasePreviewWhenIdle();
  }, [listBoxElement, listId, releasePreviewWhenIdle]);

  // An item the pointer can no longer be over must not come back as a preview once it is listed again
  const hoveredKeyInvalid = hoveredKey !== null && !processedData.keyToOriginalItem.has(hoveredKey);
  const [hoverReleaseVersion, setHoverReleaseVersion] = useState(0);
  if (hoveredKeyInvalid) {
    setHoverReleaseVersion((version) => version + 1);
    setHoveredKey(null);
  }
  useEffect(() => {
    if (hoverReleaseVersion === 0) {
      return;
    }
    hoveredKeyRef.current = null;
    releasePreviewWhenIdle();
  }, [hoverReleaseVersion, releasePreviewWhenIdle]);

  // Hover survives until the pointer leaves the declared boundary, so it holds while the pointer travels
  // to the details panel. Without a boundary the list box itself releases the hover.
  useEffect(() => {
    // Kept while the list box is unmounted, so a list without results stays a sibling of the lists around it
    const boundaryAnchor = listBoxElement ?? boundaryAnchorRef.current;
    const hoverBoundary =
      boundaryAnchor?.closest(`[${DETAILS_HOVER_BOUNDARY_ATTRIBUTE}]`) ?? listBoxElement ?? hoverBoundaryRef.current;
    if (!hoverBoundary) {
      return;
    }

    hoverBoundaryRef.current = hoverBoundary;
    joinBoundary(hoverBoundary, listId);

    const clearHoveredKey = () => {
      hoveredKeyRef.current = null;
      setHoveredKey(null);
      releasePreviewWhenIdle();
    };

    const handlePreviewClaim = (event: Event) => {
      if (!(event instanceof CustomEvent) || typeof event.detail !== 'string') {
        return;
      }

      const claimingListId = event.detail;
      setPreviewClaim((claim) => ({ listId: claimingListId, isActive: true, version: claim.version + 1 }));

      if (claimingListId !== listId) {
        holdsActiveClaimRef.current = false;
        hoveredKeyRef.current = null;
        setHoveredKey(null);
      }
    };

    const handlePreviewRelease = (event: Event) => {
      if (!(event instanceof CustomEvent) || typeof event.detail !== 'string') {
        return;
      }

      const releasingListId = event.detail;
      const releasingListIsGone = !boundaryListIds.get(hoverBoundary)?.has(releasingListId);
      setPreviewClaim((claim) => ({
        listId: releasingListIsGone ? null : releasingListId,
        isActive: false,
        version: claim.version + 1,
      }));
    };

    hoverBoundary.addEventListener('mouseleave', clearHoveredKey);
    hoverBoundary.addEventListener(DETAILS_PREVIEW_CLAIM_EVENT, handlePreviewClaim);
    hoverBoundary.addEventListener(DETAILS_PREVIEW_RELEASE_EVENT, handlePreviewRelease);

    return () => {
      hoverBoundary.removeEventListener('mouseleave', clearHoveredKey);
      hoverBoundary.removeEventListener(DETAILS_PREVIEW_CLAIM_EVENT, handlePreviewClaim);
      hoverBoundary.removeEventListener(DETAILS_PREVIEW_RELEASE_EVENT, handlePreviewRelease);
      if (holdsActiveClaimRef.current) {
        holdsActiveClaimRef.current = false;
        hoveredKeyRef.current = null;
        focusedKeyRef.current = null;
        hoverBoundary.dispatchEvent(new CustomEvent(DETAILS_PREVIEW_RELEASE_EVENT, { detail: listId }));
      }
    };
  }, [listBoxElement, listId, releasePreviewWhenIdle]);

  // A list without results has nothing to preview, so it stops counting as a sibling and hands the panel back
  useEffect(() => {
    const hoverBoundary = hoverBoundaryRef.current;
    if (!hoverBoundary || firstItemKey) {
      return;
    }

    let left = false;
    const releaseIfEmpty = () => {
      if (left || listBoxElement?.querySelector('[role="option"]')) {
        return;
      }
      left = true;
      leaveBoundary(hoverBoundary, listId);
      hoverBoundary.dispatchEvent(new CustomEvent(DETAILS_PREVIEW_RELEASE_EVENT, { detail: listId }));
    };

    releaseIfEmpty();
    const observer =
      listBoxElement && !left
        ? new MutationObserver(() => {
            releaseIfEmpty();
          })
        : null;
    observer?.observe(listBoxElement as HTMLDivElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-empty'],
    });

    return () => {
      observer?.disconnect();
      if (left && hoverBoundaryRef.current === hoverBoundary) {
        joinBoundary(hoverBoundary, listId);
      }
    };
  }, [firstItemKey, listBoxElement, listId]);

  // A list that goes away hands the panel back, or the lists left behind stay blocked by its claim
  useEffect(
    () => () => {
      const hoverBoundary = hoverBoundaryRef.current;
      if (!hoverBoundary) {
        return;
      }

      leaveBoundary(hoverBoundary, listId);
      setFocusOwner(hoverBoundary, listId, false);
      hoverBoundaryRef.current = null;
      hoverBoundary.dispatchEvent(new CustomEvent(DETAILS_PREVIEW_RELEASE_EVENT, { detail: listId }));
    },
    [listId],
  );

  const loadMore = mergedProps.onLoadMore;
  const detailsItemChange = mergedProps.onDetailsItemChange;

  // Simple scroll listener for infinite scroll
  useEffect(() => {
    if (!shouldEnableInfiniteScroll || !listBoxElement) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listBoxElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom <= 32 && loadMore) {
        loadMore();
      }
    };

    listBoxElement.addEventListener('scroll', handleScroll);

    return () => {
      listBoxElement.removeEventListener('scroll', handleScroll);
    };
  }, [shouldEnableInfiniteScroll, loadMore, listBoxElement]);

  // False while the selection belongs to a sibling list sharing the same details panel
  const holdsSelection = useMemo(() => {
    if (!mergedProps.selectedKeys || mergedProps.selectedKeys === 'all') {
      return true;
    }

    const selectedKeysArray = Array.from(mergedProps.selectedKeys);

    return (
      selectedKeysArray.length === 0 ||
      selectedKeysArray.some((key) => processedData.keyToOriginalItem.has(String(key)))
    );
  }, [mergedProps.selectedKeys, processedData.keyToOriginalItem]);

  // Details panel priority: hovered, then keyboard focused, then last selected, then first in list. A list
  // stays quiet while a sibling sharing its boundary owns the preview, to avoid overwriting it
  useEffect(() => {
    // An empty results list unmounts its list box, and still has to clear the panel
    if (!listBoxElement && firstItemKey) {
      return;
    }

    const canFallBack = holdsSelection || !hasSiblingLists(hoverBoundaryRef.current, listId);
    const claimedBySibling = previewClaim.listId !== null && previewClaim.listId !== listId;
    const ownsPreview = !!hoveredKey || !!focusedKey || !!lastSelectedKey;

    // Keyboard focus in a sibling outranks everything here but this list's own hover
    if (!hoveredKey && hasSiblingFocus(hoverBoundaryRef.current, listId)) {
      return;
    }

    // A sibling previewing outranks this list's selection, while one that just released only outranks a fallback
    if (claimedBySibling && (previewClaim.isActive || !ownsPreview)) {
      return;
    }

    if (!ownsPreview && !canFallBack) {
      return;
    }

    const isInList = (key: string | null): key is string => !!key && processedData.keyToOriginalItem.has(key);
    const detailsKey = [hoveredKey, focusedKey, lastSelectedKey, canFallBack ? firstItemKey : null].find(isInList);

    // Only a list on its own blanks the panel: a sibling sharing the boundary may still have something to show
    if (!detailsKey && hasSiblingLists(hoverBoundaryRef.current, listId)) {
      return;
    }

    detailsItemChange?.(detailsKey ? (processedData.keyToOriginalItem.get(detailsKey) ?? null) : null);
  }, [
    hoveredKey,
    focusedKey,
    lastSelectedKey,
    firstItemKey,
    holdsSelection,
    previewClaim,
    listBoxElement,
    listId,
    processedData.keyToOriginalItem,
    detailsItemChange,
  ]);

  // Enhanced children function that adds hover tracking
  const enhancedChildren = (itemWithKey: WithKey<T>) => {
    const itemKey = itemWithKey.key;
    // Get the original item without any modifications
    const originalItem = processedData.keyToOriginalItem.get(itemKey) || itemWithKey;
    const originalChild = children(originalItem);

    // Type guard to ensure we have a valid React element
    if (!isValidElement(originalChild)) {
      return originalChild;
    }

    // Add hover events and data-key for focus tracking
    const enhancedProps = {
      onHoverStart: () => {
        hoveredKeyRef.current = itemKey;
        setHoveredKey(itemKey);
        claimPreview();
        mergedProps.onDetailsItemChange?.(originalItem);
      },
      'data-key': itemKey,
    };

    return cloneElement(originalChild, enhancedProps);
  };

  // Show loading spinner if isLoading is true
  if (isLoading) {
    return (
      <div ref={setBoundaryAnchor} className={clsx(styles.resultsArea, resultsAreaClassName)}>
        <div className={styles.loadingContainer}>
          <ProgressBar aria-label={msg('ui.status.loadingDots')} isIndeterminate size="small" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div ref={setBoundaryAnchor} className={clsx(styles.resultsArea, resultsAreaClassName)}>
        <div className={styles.loadingContainer}>
          <span className={styles.errorMessage}>
            <Icon name="warning" className={styles.errorIcon} />
            <Message id="ui.searchResults.failedToLoad" />
          </span>
        </div>
      </div>
    );
  }

  // Show empty state if no items and renderEmptyState is provided
  if (itemsWithKeys.length === 0 && mergedProps.renderEmptyState) {
    return (
      <>
        <span hidden ref={setBoundaryAnchor} />
        {mergedProps.renderEmptyState()}
      </>
    );
  }

  return (
    <div ref={setBoundaryAnchor} className={clsx(styles.resultsArea, resultsAreaClassName)}>
      {processedData.isFlat ? (
        // Flat data - current behavior
        <ListBox<WithKey<T>>
          {...props}
          ref={listBoxRef}
          items={itemsWithKeys}
          selectedKeys={mergedProps.selectedKeys}
          onSelectionChange={mergedProps.onSelectionChange}
          selectionMode={mergedProps.selectionMode}
          className={composeRenderProps(props.className, (className) => clsx(className, styles.results))}
          aria-label={props['aria-label'] || 'Search results'}
        >
          {enhancedChildren}
        </ListBox>
      ) : (
        // Sectioned data - new behavior
        <ListBox
          {...props}
          ref={listBoxRef}
          selectedKeys={mergedProps.selectedKeys}
          onSelectionChange={mergedProps.onSelectionChange}
          selectionMode={mergedProps.selectionMode}
          className={composeRenderProps(props.className, (className) => clsx(className, styles.results))}
          aria-label={props['aria-label'] || 'Search results'}
        >
          {/* Some consumers intentionally provide unnamed sections; render items without an empty header row. */}
          {processedData.sectionedItems.map((section, index) => (
            <ListBoxSection key={`${section.name || 'section'}-${index}`} id={section.name || `section-${index}`}>
              {section.name ? <Header>{section.name}</Header> : null}
              {/* Forward `dependencies` so cached rows re-render when external
                  row state changes — the ListBox `{...props}` spread covers the
                  flat path, but nested Collections need it explicitly. */}
              <Collection items={section.items} dependencies={props.dependencies}>
                {enhancedChildren}
              </Collection>
            </ListBoxSection>
          ))}
        </ListBox>
      )}
      {mergedProps.isLoadingMore && (
        <div className={styles.bottomLoadingContainer}>
          <ProgressBar aria-label={msg('ui.status.loadingDots')} isIndeterminate size="small" />
        </div>
      )}
    </div>
  );
}

// Export memoized version to prevent unnecessary re-renders of expensive child components
export const SearchResults = memo(SearchResultsInternal) as typeof SearchResultsInternal;
